import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../shared/components";
import { Ticket, Users } from "lucide-react";

const ReservationSummary = ({
  selectedSeats,
  totalPrice,
  maxTicketsPerCode,
  canSelectMore,
  allSeats,
  effectiveSeatCount,
  palcoCapacity,
  isModifyingReservations = false,
  existingReservations = [],
  reservationsToCancel = [],
  reservationsToReturn = [],
  isOpenSale = false,
  isAdmin = false,
}) => {


  
  const formatPrice = (price) => {
    const numericPrice = typeof price === 'number' ? price : parseFloat(price) || 0;
    return numericPrice.toFixed(2);
  };

  const getSeatInfo = (seatId) => {
    return allSeats?.find(seat => seat.id === seatId);
  };

  const getSectionName = (seatId) => {
    if (seatId.startsWith('BOX-')) return 'Palcos';
    return seatId.startsWith('PB-') ? 'Planta Baja' : '1º Piso';
  };

  const isPalco = (seatId) => {
    const seat = getSeatInfo(seatId);
    return seat && (seat.section?.startsWith('palco') || seat.type?.toLowerCase().includes('palco'));
  };

  const formatSeatName = (seatId) => {
    // Si es un compositeId, extraer la información
    if (seatId.includes('-')) {
      const parts = seatId.split('-');
      if (parts[0] === 'BOX') {
        return `Palco ${parts[1]}`;
      } else if (parts[0] === 'PB') {
        return `Fila ${parts[1]} - Asiento ${parts[2]}`;
      } else if (parts[0] === 'PP') {
        return `Fila ${parts[1]} - Asiento ${parts[2]}`;
      }
    }
    
    // Si no es un compositeId, buscar en allSeats
    const seat = allSeats.find(s => s.compositeId === seatId) || allSeats.find(s => s.id === seatId);
    if (seat) {
      if (seat.section?.startsWith('palco') || seat.type?.toLowerCase().includes('palco')) {
        return `Palco ${seat.number}`;
      } else {
        return `Fila ${seat.row} - Asiento ${seat.number}`;
      }
    }
    
    // Fallback: mostrar el ID original
    return seatId;
  };

  const groupedSeats = selectedSeats.reduce((acc, seatId) => {
    const sectionName = getSectionName(seatId);
    if (!acc[sectionName]) {
      acc[sectionName] = [];
    }
    acc[sectionName].push(seatId);
    return acc;
  }, {});



  // Calcular precio total considerando devoluciones (precios negativos)
  const calculateEffectiveTotalPrice = () => {
    let total = 0;
    
    // Sumar precio de asientos seleccionados
    selectedSeats.forEach(seatId => {
      const seat = allSeats.find(s => s.compositeId === seatId);
      if (seat) {
        total += seat.price || 0;
      }
    });
    
    // Si estamos modificando reservas, considerar reservas pendientes existentes y devoluciones
    if (isModifyingReservations && existingReservations.length > 0) {
      existingReservations.forEach(reservation => {
        const isMarkedForReturn = reservationsToReturn.some(r => r.id === reservation.id);
        const isMarkedForCancel = reservationsToCancel.some(r => r.id === reservation.id);
        
        // Si es una reserva pendiente y no está marcada para cancelación, incluirla en el total
        if (reservation.status === 'pending' && !isMarkedForCancel && reservation.price) {
          total += reservation.price;
        }
        
        // Si la reserva está marcada para devolución y era 'sold' o 'delivered', restar su precio (efecto negativo)
        if (isMarkedForReturn && (reservation.status === 'sold' || reservation.status === 'delivered') && reservation.price) {
          total -= reservation.price;
        }
      });
    }
    
    return total;
  };

  // Calcular el conteo total de asientos seleccionados (incluyendo gratuitos para la UI)
  // El effectiveSeatCount del hook excluye entradas gratuitas para el límite
  const totalSelectedSeats = selectedSeats.length;
  const displaySeatCount = totalSelectedSeats;
  
  // Usar el totalPrice pasado como prop en lugar del cálculo interno para casos especiales
  // como TicketSalesDashboard que tiene lógica más compleja
  const internalCalculatedPrice = calculateEffectiveTotalPrice();
  const displayTotalPrice = typeof totalPrice === 'number' ? totalPrice : internalCalculatedPrice;

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
          <Ticket className="w-4 h-4 sm:w-5 sm:h-5" />
          {isModifyingReservations ? 'Modificar Reserva' : 'Resumen de Reserva'}
        </CardTitle>
        <CardDescription className="text-xs sm:text-sm">
          {isModifyingReservations 
            ? 'Modifica tu selección de asientos' 
            : 'Asientos seleccionados y precio total'
          }
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Información de límite */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <Users className="w-4 h-4" />
            <span>
              {displaySeatCount} asientos seleccionados
            </span>
          </div>
          
          {/* Información sobre el límite */}
          {!isAdmin && !isOpenSale && effectiveSeatCount !== undefined && (
            <div className="text-xs text-gray-600 bg-blue-50 p-2 rounded border border-blue-200">
              <p>
                <strong>Límite de entradas:</strong> {effectiveSeatCount} / {maxTicketsPerCode}
              </p>
              {canSelectMore && (
                <p className="text-green-600 font-medium mt-1">
                  ✓ Puedes seleccionar más asientos
                </p>
              )}
            </div>
          )}
          
          {/* Información para administradores */}
          {isAdmin && (
            <div className="text-xs text-gray-600 bg-purple-50 p-2 rounded border border-purple-200">
              <p className="text-purple-700 font-medium">
                ✓ Modo Administrador - Sin límite de entradas
              </p>
            </div>
          )}
          
          {/* Información para venta global */}
          {!isAdmin && isOpenSale && (
            <div className="text-xs text-gray-600 bg-green-50 p-2 rounded border border-green-200">
              <p className="text-green-700 font-medium">
                ✓ Venta Global - Sin límite de entradas por código
              </p>
            </div>
          )}
        </div>

        {/* Asientos seleccionados por sección */}
        {Object.keys(groupedSeats).length > 0 ? (
          <div className="space-y-3">
            {Object.entries(groupedSeats).map(([sectionName, seatIds]) => (
              <div key={sectionName} className="border rounded-lg p-3">
                <h4 className="font-semibold text-sm mb-2">{sectionName}</h4>
                <div className="space-y-1">
                  {seatIds.map((seatId) => {
                    const seatInfo = getSeatInfo(seatId);
                    const isPalcoSeat = isPalco(seatId);
                    
                    // Verificar si es una reserva existente con status 'sold'
                    const existingSoldReservation = existingReservations.find(r => 
                      r.compositeId === seatId && r.status === 'sold'
                    );
                    
                    return (
                      <div key={seatId} className={`flex justify-between items-center text-xs ${
                        existingSoldReservation ? 'bg-gray-100 p-2 rounded border-l-4 border-green-500' : ''
                      }`}>
                        <span>
                          {sectionName === 'Palcos' || isPalcoSeat
                            ? `Palco ${seatInfo?.number}`
                            : `Fila ${seatInfo?.row} - Asiento ${seatInfo?.number}`}
                          {isPalcoSeat && palcoCapacity > 1 && (
                            <span className="text-gray-500 ml-1">
                              (×{palcoCapacity})
                            </span>
                          )}
                          {existingSoldReservation && (
                            <span className="text-green-600 font-medium ml-2">
                              ✓
                            </span>
                          )}
                        </span>
                        <span className={`font-medium ${
                          existingSoldReservation ? 'text-green-600' : ''
                        }`}>
                          {(existingSoldReservation && parseFloat(existingSoldReservation.price) === 0) || 
                           (seatInfo && parseFloat(seatInfo.price) === 0)
                            ? '$0' 
                            : `$${formatPrice(seatInfo?.price)}`
                          }
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center text-gray-500 text-sm py-4">
            No hay asientos seleccionados
          </div>
        )}

        {/* Total */}
        <div className="border-t pt-2 mt-2">
          <div className="flex justify-between font-semibold">
            <span>Total:</span>
            <span className={displayTotalPrice < 0 ? 'text-red-600' : ''}>
              {/* $ {displayTotalPrice.toLocaleString()} */}
              $ {displayTotalPrice < 0 ? '0' : displayTotalPrice.toLocaleString()}
            </span>
          </div>
        </div>

        {/* Mensaje de límite */}
        {!isAdmin && !isOpenSale && !canSelectMore && selectedSeats.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-yellow-800 text-sm">
              Seleccionaste el máximo de asientos por código.
            </p>
          </div>
        )}

          {/* Resumen de cambios si estamos modificando */}
          {isModifyingReservations && (reservationsToCancel.length > 0 || reservationsToReturn.length > 0) && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <h4 className="font-medium text-blue-800 text-sm mb-2">Resumen de cambios:</h4>
              <div className="space-y-2 text-xs">
                {reservationsToCancel.length > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                    <span className="text-yellow-700">
                      {reservationsToCancel.length} asiento(s) a cancelar
                    </span>
                  </div>
                )}
                {reservationsToReturn.length > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                    <span className="text-orange-700">
                      {reservationsToReturn.length} asiento(s) a devolver
                    </span>
                  </div>
                )}
              </div>
              
              {/* Mostrar precios de devoluciones */}
              {reservationsToReturn.length > 0 && (
                <div className="mt-3 pt-3 border-t border-blue-200">
                  <h5 className="font-medium text-blue-800 text-xs mb-2">Devoluciones:</h5>
                  <div className="space-y-1">
                    {reservationsToReturn.map(reservation => {
                      const seat = allSeats.find(s => s.compositeId === reservation.compositeId);
                      return (
                        <div key={reservation.id} className="flex justify-between text-xs">
                          <span className="text-orange-700">
                            {formatSeatName(reservation.compositeId)}
                          </span>
                          <span className="text-red-600 font-medium">
                            {reservation.price === 0 
                              ? '$0' 
                              : `-$${(reservation.price || seat?.price || 0).toLocaleString()}`
                            }
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
      </CardContent>
    </Card>
  );
};

export default ReservationSummary; 